import { DateTime } from 'luxon';

import { SNAP_MINUTES } from '@/lib/motion';
import type { TimeBlock } from './types';

/**
 * Work that was planned before the day being planned and never finished.
 *
 * The planner opens on tomorrow, which means the day you are arranging is the
 * one *after* the day that just went sideways. Anything still sitting on
 * yesterday's grid is invisible here, the task reads as "scheduled", so it is
 * not in the backlog either, and it quietly falls out of the plan entirely.
 * These helpers pull it back into view so it can be moved forward.
 */

/** How far back the planner looks for unfinished work. */
export const CARRY_LOOKBACK_DAYS = 7;

/** Statuses that can still be carried: the work has not been resolved. */
export const CARRY_STATUSES: TimeBlock['status'][] = ['proposed', 'confirmed'];

/** Query window for unfinished blocks sitting before `dayStart`. */
export function carryOverWindow(
  dayStart: Date,
  lookbackDays = CARRY_LOOKBACK_DAYS,
): { start: Date; end: Date } {
  const end = new Date(dayStart);
  const start = new Date(end.getTime() - lookbackDays * 86_400_000);
  return { start, end };
}

/** Planned length of a block, in minutes. */
export function blockMinutes(block: TimeBlock): number {
  const ms =
    DateTime.fromISO(block.ends_at).toMillis() -
    DateTime.fromISO(block.starts_at).toMillis();
  return Math.max(SNAP_MINUTES, Math.round(ms / 60_000));
}

function compactDuration(minutes: number): string {
  if (minutes <= 0) return '-';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export interface CarryGroup {
  key: string;
  /** `Today`, `Yesterday`, or `Mon, Aug 24`. */
  label: string;
  /** Right-aligned summary: `2 · 1h 30m`. */
  meta: string;
  /** Older than yesterday: it has been slipping for a while. */
  stale: boolean;
  blocks: TimeBlock[];
}

/**
 * Group leftover blocks by the day they were planned for, oldest first.
 *
 * Oldest first on purpose: the thing that has been pushed three times is the
 * thing most likely to be pushed again, and it should not be at the bottom of
 * the list.
 */
export function groupCarryOver(
  blocks: TimeBlock[],
  ctx: { timezone: string; now?: DateTime },
): CarryGroup[] {
  const now = (ctx.now ?? DateTime.now()).setZone(ctx.timezone);
  const today = now.startOf('day');
  const byDay = new Map<string, TimeBlock[]>();

  for (const block of blocks) {
    const day =
      DateTime.fromISO(block.starts_at).setZone(ctx.timezone).toISODate() ?? '';
    const bucket = byDay.get(day);
    if (bucket) bucket.push(block);
    else byDay.set(day, [block]);
  }

  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, dayBlocks]) => {
      const at = DateTime.fromISO(day, { zone: ctx.timezone });
      const offset = Math.round(at.diff(today, 'days').days);
      const label =
        offset === 0
          ? 'Today'
          : offset === -1
            ? 'Yesterday'
            : at.toFormat('ccc, LLL d');
      const minutes = dayBlocks.reduce((sum, b) => sum + blockMinutes(b), 0);
      const sorted = [...dayBlocks].sort((a, b) =>
        a.starts_at.localeCompare(b.starts_at),
      );
      return {
        key: day,
        label,
        meta: `${sorted.length} · ${compactDuration(minutes)}`,
        stale: offset < -1,
        blocks: sorted,
      };
    });
}

/**
 * Where a leftover block lands when moved to the planned day in one click.
 *
 * Same clock time on the new day, the hour it was planned for is usually the
 * hour it belongs in. The one exception is a slot that has already gone by
 * (planning *today*, at noon, a block from 9am): scheduling into the past is
 * never useful, so it lands at the next snap point instead.
 */
export function carryTarget(
  block: TimeBlock,
  dayStart: Date,
  timezone: string,
  now: DateTime = DateTime.now(),
): { startsAt: Date; endsAt: Date } {
  const from = DateTime.fromISO(block.starts_at).setZone(timezone);
  const day = DateTime.fromJSDate(dayStart).setZone(timezone).startOf('day');
  let start = day.set({
    hour: from.hour,
    minute: from.minute,
    second: 0,
    millisecond: 0,
  });

  const zoneNow = now.setZone(timezone);
  if (start <= zoneNow) {
    // Strictly forward: a slot starting this second is not a slot you can plan into.
    const snapped = (Math.floor(zoneNow.minute / SNAP_MINUTES) + 1) * SNAP_MINUTES;
    start = zoneNow.set({ minute: 0, second: 0, millisecond: 0 }).plus({
      minutes: snapped,
    });
  }

  return {
    startsAt: start.toJSDate(),
    endsAt: start.plus({ minutes: blockMinutes(block) }).toJSDate(),
  };
}
